import * as React from "react";
import "./ProvisionPage.scss";
import ProvisionProgressDlg from "./ProvisionProgressDlg";
import Api from "./Api";
import PlainDate from "@testout/testout-commerce/models/PlainDate";
import LabelCtl from "./input/LabelCtl";
import DateCtl from "./input/DateCtl";
import LookupCtl from "./input/LookupCtl";
import EmailCtl from "./input/EmailCtl";
import SelectCtl from "./input/SelectCtl";
import AddCampusDlg from "./AddCampusDlg";
import { IProvision, IProvisionCompany } from "@testout/testout-commerce/models/Provision";
import { Deal } from "@testout/testout-commerce/models/hubspot/Deal";
import { Company } from "@testout/testout-commerce/models/hubspot/Company";
import { Contact } from "@testout/testout-commerce/models/hubspot/Contact";
import { PostalAddress } from "@testout/testout-commerce/models/commerce/PostalAddress";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { Email } from "@testout/testout-commerce/models/Email";
import Segment from "@testout/testout-commerce/models/Segment";
import { Product, ProductType, ProductStatus, IProduct } from "@testout/testout-commerce/models/commerce/Product";
import CheckCtl from "./input/CheckCtl";
import { ContactEmail } from "@testout/testout-commerce/models/ContactEmail";

interface ProvisionPageProps {
    objectId?: string;
    objectType?: string;
}

interface ProvisionPageState {
    provision: IProvision;
    products: ListItem[];
    showProgressDlg: boolean;
    addCampusIndex: number;
    errors: string[];
    isLoading: boolean;
    provisionEnabled: boolean;
}

export default class ProvisionPage extends React.PureComponent<ProvisionPageProps, ProvisionPageState> {
    constructor(props: ProvisionPageProps) {
        super(props);
        this.state = {
            provision: { companyId: "", companies: [], products: [] },
            products: [],
            showProgressDlg: false,
            addCampusIndex: -1,
            errors: [],
            isLoading: true,
            provisionEnabled: false
        };
    }

    onProductsChange = (value: string) => {
        this.setState({ provision: { ...this.state.provision, products: value.split(",") } });
    }

    onExpirationDateChange = (value?: Date) => {
        this.setState({ provision: { ...this.state.provision, expirationDate: value } });
    }

    onUseInstructorLicenseChange = (value?: boolean) => {
        this.setState({ provision: { ...this.state.provision, useInstructorLicense: value } });
    }

    onDoCompanySearch = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/companies/listitems/${value}`)]
            : await Api.get<ListItem[]>(`/api/companies/listitems?search=${value}`);
    }

    onCompanyIdChange = (campusIndex: number, value?: string, data?: {[key: string]: string|undefined}) => {
        const companies = [...this.state.provision.companies];
        companies[campusIndex] = { ...companies[campusIndex], companyId: value ?? "", 
            customerNumber: data?.["customerNumber"], 
            campusProfileId: data?.["campusProfileId"],
            name: data?.["name"],
            postal: JSON.parse((data?.["postal"] ?? "") as string)
        };
        this.setState({ provision: { ...this.state.provision, companies: companies } });
    }

    onAddCampusDlgOpen = (campusIndex: number) => {
        this.setState({ addCampusIndex: campusIndex });
    }

    onAddCampusDlgClose = (value?: string) => {
        const campusIndex = this.state.addCampusIndex;
        this.setState({ addCampusIndex: -1 }, () => {
            if (value) {
                const companies = [...this.state.provision.companies];
                companies[campusIndex] = { ...companies[campusIndex], campusProfileId: value ?? "" };
                this.setState({ provision: { ...this.state.provision, companies: companies } });
            }
        });
    }

    onDoCampusSearch = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/campuses/${value}/list-item`)]
            : await Api.get<ListItem[]>(`/api/campuses/list-items?search=${value}`);
    }

    onCampusProfileIdChange = (campusIndex: number, value?: string) => {
        const companies = [...this.state.provision.companies];
        companies[campusIndex] = { ...companies[campusIndex], campusProfileId: value ?? "" };
        this.setState({ provision: { ...this.state.provision, companies: companies } });
    }

    private _abort?: AbortController;
    onCustomerEmailsSelect = async (companyIndex: number, value: string): Promise<ContactEmail[]|null> => {
        // Abort any in progress api call for email addresses
        if (this._abort) this._abort.abort();
        this._abort = new AbortController();
        const companyId = this.state.provision.companies[companyIndex].companyId;
        try {
            return await Api.get(`/api/contacts/emails?search=${value}${companyId ? `&companyId=${companyId}` : ""}`, this._abort.signal);
        } catch (error: any) {
            if (error.name === "AbortError")
                return null;
            else
                throw error;
        }
    }

    onCustomerEmailsChange = (campusIndex: number, value?: string) => {
        const companies = [...this.state.provision.companies];
        companies[campusIndex] = { ...companies[campusIndex], emails: value ?? "" };
        this.setState({ provision: { ...this.state.provision, companies: companies } });
    }

    onMakeTeachersAdminsChange = (campusIndex: number, value?: boolean) => {
        const companies = [...this.state.provision.companies];
        companies[campusIndex] = { ...companies[campusIndex], makeTeachersAdmins: value ?? true };
        this.setState({ provision: { ...this.state.provision, companies: companies } });
    }

    onAddCustomer = (value: string, data?: {[key: string]: string|undefined}) => {
        const companies = [...this.state.provision.companies];
        companies.push({ companyId: value, 
            customerNumber: data?.["customerNumber"], 
            campusProfileId: data?.["campusProfileId"], 
            name: data?.["name"],
            postal: JSON.parse(data?.["postal"] ?? "" as string),
            emails: "" 
        });
        this.setState({ provision: { ...this.state.provision, companies: companies } });
    }

    onRemoveCustomer = (campusIndex: number) => {
        const companies = [...this.state.provision.companies];
        companies.splice(campusIndex, 1);
        this.setState({ provision: { ...this.state.provision, companies: companies } });
    }

    onProvisionClick = () => {
        const { provision } = this.state;
        const errors: string[] = [];
        if (!provision.expirationDate || provision.expirationDate < PlainDate.today)
            errors.push("Enter an expiration date that is in the future.");
        if (provision.companies.length === 0)
            errors.push("Add at least one company to provision.");
        if (provision.products.length === 0)
            errors.push("Add at least one product to provision.");
        for (const company of provision.companies) {
            if (!company.emails)
                errors.push(`Enter at least one email for ${company.name}.`);
            if (!company.campusProfileId)
                errors.push(`Select or add a campus for ${company.name}`);
        }

        if (errors.length === 0)
            this.setState({ showProgressDlg: true, errors: [] });
        else
            this.setState({ errors: errors });
    }

    onProvisionClose = (failed: boolean) => {
        this.setState({ showProgressDlg: false }, () => {
            if (!failed)
                window.parent.postMessage(JSON.stringify({action: "DONE"}), "*");
        });
    }

    onCancelClick = () => {
        window.parent.postMessage(JSON.stringify({action: "CANCEL"}), "*");
    }

    async loadProducts() {
        try {
            const result = await Api.get<Product[]>("/api/products");
            this.setState({
                products: result
                    .map((product: IProduct) => ({
                        ...product,
                        status: product.status || ProductStatus.Active,
                        productType: product.productType || ProductType.DigitalGoods,
                        culture: product.culture || "en-US"
                    }))
                    .filter((product: IProduct) =>
                        product.status === ProductStatus.Active &&
                        product.productType === ProductType.DigitalGoods &&
                        product.unitType === "License" &&
                        !!product.courseId)
                    .sort((a, b) => a.culture !== "en-US" && b.culture === "en-US" ? 1 : a.culture === "en-US" && b.culture !== "en-US" ? -1 : a.title > b.title ? 1 : a.title < b.title ? -1 : 0)
                    .map((product: IProduct) => ({
                        value: product.productNumber,
                        name: product.title,
                        data: { courseId: product.courseId?.toString(), familyCode: product.familyCode }
                    }))
            });
        }
        catch (error: any) {
            this.setState({ errors: [...this.state.errors, `The list of products failed to load. ${error.message}`] });
        }
    }

    async loadProvision() {
        try {
            let expirationDate: Date;
            let provision: IProvision;
            let isSecondaryEd: boolean;
            switch (this.props.objectType) {
                case "Deal":
                    const deal = await Api.get<Deal>(`/api/deals/${this.props.objectId}?getContacts=true`);
                    if (deal.companies.length === 0)
                        throw new Error("Ensure the deal is associated with a company before provisioning.");

                    // Expire at end of academic year (Sep 30) for SecondaryEd or 12 months for HigherEd
                    isSecondaryEd = new Segment(deal.companies[0].segment ?? "").parentSegment?.code === Segment.secondaryEd.code;
                    expirationDate = isSecondaryEd
                        ? new Date(PlainDate.today.getFullYear() + (PlainDate.today.getMonth() > 2 ? 1 : 0), 8, 30)     // 8 = September, 9 = October
                        : new Date(PlainDate.today.getFullYear() + 1, PlainDate.today.getMonth() + 1, 1);

                    provision = {
                        companyId: deal.companies[0].hs_object_id,
                        customerNumber: deal.companies[0].d365_account_number,
                        campusProfileId: deal.companies[0].labsim6_companyprofileid,
                        companies: [{ 
                            name: deal.companies[0].name, 
                            postal: {
                                line1: deal.companies[0].address,
                                line2: deal.companies[0].address2,
                                city: deal.companies[0].city,
                                stateOrProvince: deal.companies[0].state,
                                postalCode: deal.companies[0].zip,
                                countryCode: deal.companies[0].country ?? "USA"
                            },
                            companyId: deal.companies[0].hs_object_id, 
                            customerNumber: deal.companies[0].d365_account_number,
                            campusProfileId: deal.companies[0].labsim6_companyprofileid,
                            emails: deal.contacts.map(contact => new Email(contact.email, contact.firstname + " " + contact.lastname).toString()).join(","),
                            makeTeachersAdmins: isSecondaryEd
                        }],
                        products: [...new Set(deal.line_items.map(item => item.hs_sku ?? "").filter(sku => (this.state.products.find(item => item.value === sku)) ))],
                        expirationDate: expirationDate,
                        useInstructorLicense: false,
                        dealId: deal.hs_object_id
                    };
                    this.setState({ provision: provision, isLoading: false, provisionEnabled: true });
                    break;
                case "Company":
                    const company = await Api.get<Company>(`/api/companies/${this.props.objectId}`);

                    // Expire at end of academic year (Sep 30) for SecondaryEd or 12 months for HigherEd
                    isSecondaryEd = new Segment(company.segment ?? "").parentSegment?.code === Segment.secondaryEd.code;
                    expirationDate = isSecondaryEd
                        ? new Date(PlainDate.today.getFullYear() + (PlainDate.today.getMonth() > 2 ? 1 : 0), 8, 30)     // 8 = September, 9 = October
                        : new Date(PlainDate.today.getFullYear() + 1, PlainDate.today.getMonth() + 1, 1);

                    const prospectiveFamilyCodes: string[] = (company.prospective_products || "").split(",");
                    const prospectiveProductNumbers: string[] = this.state.products
                        .filter(product => prospectiveFamilyCodes.indexOf(String(product.data?.["familyCode"])) >= 0)
                        .map(product => product.value || "");

                    provision = {
                        companyId: company.hs_object_id,
                        customerNumber: company.d365_account_number,
                        campusProfileId: company.labsim6_companyprofileid,
                        companies: [{ 
                            name: company.name, 
                            postal: {
                                line1: company.address,
                                line2: company.address2,
                                city: company.city,
                                stateOrProvince: company.state,
                                postalCode: company.zip,
                                countryCode: company.country ?? "USA"
                            },
                            companyId: company.hs_object_id, 
                            customerNumber: company.d365_account_number,
                            campusProfileId: company.labsim6_companyprofileid,
                            emails: company.contacts.map(contact => new Email(contact.email, contact.firstname + " " + contact.lastname).toString()).join(","),
                            makeTeachersAdmins: isSecondaryEd
                        }],
                        products: [...new Set(prospectiveProductNumbers)],
                        expirationDate: expirationDate,
                        useInstructorLicense: false
                    };
                    this.setState({ provision: provision, isLoading: false, provisionEnabled: true });
                    break;
                case "Contact":
                    const contact = await Api.get<Contact>(`/api/contacts/${this.props.objectId}`);
                    if (!contact.associatedcompanyid)
                        throw new Error("Ensure the contact is associated with a company before provisioning.");
                    const contactCompany = await Api.get<Company>(`/api/companies/${contact.associatedcompanyid}`);

                    // Expire at end of academic year (Sep 30) for SecondaryEd or 12 months for HigherEd
                    isSecondaryEd = new Segment(contactCompany.segment ?? "").parentSegment?.code === Segment.secondaryEd.code;
                    expirationDate = isSecondaryEd
                        ? new Date(PlainDate.today.getFullYear() + (PlainDate.today.getMonth() > 2 ? 1 : 0), 8, 30)     // 8 = September, 9 = October
                        : new Date(PlainDate.today.getFullYear() + 1, PlainDate.today.getMonth() + 1, 1);

                    const prospectiveFamilyCodes2: string[] = (contact.prospective_products || "").split(",");
                    const prospectiveProductNumbers2: string[] = this.state.products
                        .filter(product => prospectiveFamilyCodes2.indexOf(String(product.data?.["familyCode"])) >= 0)
                        .map(product => product.value || "");

                    provision = {
                        companyId: contactCompany.hs_object_id,
                        customerNumber: contactCompany.d365_account_number,
                        campusProfileId: contactCompany.labsim6_companyprofileid,
                        companies: [{ 
                            name: contactCompany.name, 
                            postal: {
                                line1: contactCompany.address,
                                line2: contactCompany.address2,
                                city: contactCompany.city,
                                stateOrProvince: contactCompany.state,
                                postalCode: contactCompany.zip,
                                countryCode: contactCompany.country ?? "USA"
                            },
                            companyId: contactCompany.hs_object_id, 
                            customerNumber: contactCompany.d365_account_number,
                            campusProfileId: contactCompany.labsim6_companyprofileid,
                            emails: new Email(contact.email, contact.firstname + " " + contact.lastname).toString(),
                            makeTeachersAdmins: isSecondaryEd
                        }],
                        products: [...new Set(prospectiveProductNumbers2)],
                        expirationDate: expirationDate,
                        useInstructorLicense: false
                    };
                    this.setState({ provision: provision, isLoading: false, provisionEnabled: true });
                    break;
                default:
                    this.setState({ errors: [...this.state.errors, `Provision from ${this.props.objectType} is not supported.`] });
                    break;
            }
        }
        catch (error: any) {
            console.log(`exception ${error}`);
            this.setState({ errors: [...this.state.errors, `The ${this.props.objectType} properties failed to load. ${error.message}`] });
        }
    }

    async componentDidMount() {
        try {
            await this.loadProducts();
            await this.loadProvision();
        }
        finally {
            this.setState({ isLoading: false });
        }
    }

    render() {
        const { provision, addCampusIndex } = this.state;

        return this.state.isLoading
            ? <div className="loading">
                  <div>Loading...
                      <div className="spinner"></div></div></div>
            : <div className="page ProvisionPage">

                  <header>
                      <p>Select the LabSim products and expiration date, and enter teacher email addresses to provision.
                          Each teacher will be given access to the products and sent a welcome email with login credentials.</p>
                      {this.state.errors.length > 0 &&
                          <p className="error">{this.state.errors.join(" ")}</p>}
                  </header>

                  <main>
                      <section className="common">
                          <LabelCtl label="Products">
                              <SelectCtl placeholder="Select products" multiSelect={true} hideItemValue={true}
                                         value={provision.products.join(",")} onChange={this.onProductsChange}
                                         items={this.state.products}/>
                          </LabelCtl>
                          <LabelCtl label="Expiration Date">
                              <DateCtl placeholder="MM/DD/YYYY" value={provision.expirationDate} onChange={this
                                  .onExpirationDateChange}/>
                              <CheckCtl label="Use Instructor License"
                                        value={provision.useInstructorLicense}
                                        onChange={this.onUseInstructorLicenseChange}/>
                          </LabelCtl>
                      </section>

                      <section className="teachers">
                          <h3>Teachers</h3>
                          <table>
                              <thead>
                              <tr>
                                  <th className="item-delete">&nbsp;</th>
                                  <th className="item-campus">Company</th>
                                  <th className="item-emails">Name &lt;Email&gt; List</th>
                              </tr>
                              </thead>
                              <tbody>

                              {provision.companies.map((campus: IProvisionCompany, i) =>
                                  <tr key={i}>
                                      <td>
                                          <button className="trash"
                                                  onClick={() => this.onRemoveCustomer(i)}>&nbsp;</button></td>
                                      <td>
                                          <LookupCtl placeholder="Company"
                                                     onSearch={this.onDoCompanySearch} hideItemValue={true}
                                                     value={campus.companyId}
                                                     onChange={(value, data) => this.onCompanyIdChange(i, value, data)}/>
                                          <LabelCtl label="Campus" className="campus" useDiv={true}>
                                              <button className="link"
                                                      onClick={() => this.onAddCampusDlgOpen(i)}>+ Add</button>
                                              <LookupCtl placeholder="Campus"
                                                         onSearch={this.onDoCampusSearch} hideItemValue={true}
                                                         value={campus.campusProfileId}
                                                         onChange={(value) => this.onCampusProfileIdChange(i, value)}/>
                                          </LabelCtl>
                                      </td>
                                      <td>
                                          <EmailCtl placeholder="Email List" value={campus.emails}
                                                    onChange={(value) => this.onCustomerEmailsChange(i, value)}
                                                    onSelect={(value) => this.onCustomerEmailsSelect(i, value)}/>
                                          <CheckCtl label="Make these teachers campus administrators"
                                                    value={campus.makeTeachersAdmins}
                                                    onChange={(value) => this.onMakeTeachersAdminsChange(i, value)}/>
                                      </td>
                                  </tr>)}

                              <tr>
                                  <td>&nbsp;</td>
                                  <td>
                                      <LookupCtl key="_add_" placeholder="Add company"
                                                 onSearch={this.onDoCompanySearch} onChange={this.onAddCustomer}
                                                 hideItemValue={true}/></td>
                                  <td></td>
                              </tr>
                              </tbody>
                          </table>
                      </section>
                  </main>

                  <footer>
                      <button className="secondary" onClick={this.onCancelClick}>Cancel</button>
                      <button className="primary" onClick={this.onProvisionClick}
                              disabled={!this.state.provisionEnabled}>Provision</button>
                  </footer>

                  <ProvisionProgressDlg provision={this.state.provision} products={this.state.products}
                                        isOpen={this.state.showProgressDlg} onClose={this.onProvisionClose}/>
                  <AddCampusDlg defaultName={addCampusIndex >= 0
                      ? provision.companies[addCampusIndex].name
                      : undefined}
                                defaultPostal={addCampusIndex >= 0
                                    ? new PostalAddress(provision.companies[addCampusIndex].postal)
                                    : undefined}
                                isOpen={addCampusIndex >= 0} onClose={this.onAddCampusDlgClose}/>

              </div>;
    }
}